/**
 * Extracts a user-friendly error message from an API error response
 *
 * @param error - The error object from a catch block
 * @param defaultMessage - Default message to show if no specific error message is found
 * @returns A user-friendly error message string
 */
export const getErrorMessage = (error: any, defaultMessage: string = 'An error occurred. Please try again.'): string => {
  // Check if error has a response from the API
  if (error?.response?.data) {
    const data = error.response.data;

    // Check for the standard backend error format
    // { "message": "...", "error": "...", "fieldErrors": [...] }
    if (data.message) {
      return data.message;
    }

    // Check for field errors (validation errors)
    if (data.fieldErrors && Array.isArray(data.fieldErrors) && data.fieldErrors.length > 0) {
      // Return the first field error message
      const firstError = data.fieldErrors[0];
      return firstError.message || firstError.defaultMessage || defaultMessage;
    }

    // Check for generic error field
    if (data.error && typeof data.error === 'string') {
      return data.error;
    }

    // If data itself is a string
    if (typeof data === 'string') {
      return data;
    }
  }

  // Check for axios error message
  if (error?.message) {
    return error.message;
  }

  // Return the default message
  return defaultMessage;
};

/**
 * Formats field errors into a readable message
 *
 * @param error - The error object from a catch block
 * @returns A formatted string with all field errors, or null if no field errors exist
 */
export const getFieldErrorsMessage = (error: any): string | null => {
  if (error?.response?.data?.fieldErrors && Array.isArray(error.response.data.fieldErrors)) {
    const fieldErrors = error.response.data.fieldErrors;
    if (fieldErrors.length === 0) return null;

    // Format multiple field errors
    if (fieldErrors.length === 1) {
      const err = fieldErrors[0];
      return `${err.field}: ${err.message || err.defaultMessage}`;
    }

    return fieldErrors
      .map((err: any) => `${err.field}: ${err.message || err.defaultMessage}`)
      .join(', ');
  }

  return null;
};
